import { useCallback, useEffect, useState } from 'react';
import {
  geocodeAddress,
  fetchSatelliteGround,
  fetchGroundElevation,
  DEFAULT_GROUND_SQ_MI,
  sqMiToSideMeters,
} from '../services/googleMapsSite';
import { fetchOsmBuildings, rectangleVertices } from '../services/osmBuildings';
import { clearFloorCache } from '../services/photorealisticFloor';
import {
  getTileSnapshotBudget,
  ladderNoteForStep,
  nextLadderAfterTilesFail,
} from '../services/photorealisticLoadLadder';
import { presetLot } from '../utils/plotGeom';
import { ftToM } from '../utils/unitsUs';

/** Default lot matches Construx Site (18×22 m ≈ 59×72 ft). See docs/XLABS_LOCATE.md */
const DEFAULT_LOT = { vertices: rectangleVertices(0, 0, 18, 22), y: 0 };

/**
 * Locate-stage site state — Construx B-stack (page state, not Redux).
 * Copies proven loadSite / floor-lock wiring from SpeckleViewerPage.
 * Photorealistic ladder: docs/PHOTOREALISTIC_MOBILE_FALLBACK.md
 */
export default function useSiteLocateState() {
  const [address, setAddress] = useState('');
  const [groundSqMi, setGroundSqMi] = useState(DEFAULT_GROUND_SQ_MI);
  const [siteLoading, setSiteLoading] = useState(false);
  const [siteError, setSiteError] = useState(null);
  const [site, setSite] = useState(null);
  const [lot, setLot] = useState(DEFAULT_LOT);
  const [mapBuildings, setMapBuildings] = useState([]);
  const [contextMode, setContextMode] = useState('google');
  /** 0=full, 1=half, 2=quarter Photorealistic snapshot. */
  const [tileSnapshotLevel, setTileSnapshotLevel] = useState(0);
  /** Soft recoverable note from the load ladder (not a hard siteError). */
  const [contextNote, setContextNote] = useState(null);
  const [viewLocked, setViewLocked] = useState(false);
  const [lotSelected, setLotSelected] = useState(false);
  const [floorY, setFloorY] = useState(null);
  const [floorDebug, setFloorDebug] = useState(null);

  const [setback, setSetback] = useState(() => ftToM(5)); // 5 ft
  const [northDeg, setNorthDeg] = useState(0);
  const [tool, setTool] = useState('select');
  const [snap, setSnap] = useState(() => ({ enabled: true, size: ftToM(1) })); // 1 ft
  const [selectedVertexIndex, setSelectedVertexIndex] = useState(null);

  useEffect(() => {
    return () => {
      if (site?.objectUrl) URL.revokeObjectURL(site.objectUrl);
    };
  }, [site?.objectUrl]);

  // OSM step with no footprints → default 3D (ladder step 5).
  useEffect(() => {
    if (contextMode !== 'osm') return;
    if (mapBuildings.length > 0) return;
    setContextMode('off');
    setFloorY((y) => (y == null || !Number.isFinite(y) ? 0 : y));
    setContextNote(ladderNoteForStep('off'));
  }, [contextMode, mapBuildings.length]);

  const loadSite = useCallback(async (addressOverride) => {
    const query =
      typeof addressOverride === 'string' && addressOverride.trim()
        ? addressOverride.trim()
        : address.trim();
    if (!query) {
      setSiteError('Enter an address to load');
      return;
    }
    setSiteError(null);
    setSiteLoading(true);
    const groundMeters = sqMiToSideMeters(groundSqMi);
    try {
      const geo = await geocodeAddress(query);
      let elevationMeters = 0;
      try {
        elevationMeters = await fetchGroundElevation(geo.lat, geo.lng);
      } catch (elevErr) {
        console.warn('Ground elevation unavailable', elevErr);
      }
      const sat = await fetchSatelliteGround(geo.lat, geo.lng, groundMeters);
      let buildings = [];
      try {
        buildings = await fetchOsmBuildings(
          geo.lat,
          geo.lng,
          sat.groundMeters / 2,
        );
      } catch (osmErr) {
        console.warn('OSM buildings unavailable', osmErr);
      }
      setSite((prev) => {
        if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
        return {
          ...geo,
          elevationMeters,
          objectUrl: sat.objectUrl,
          groundMeters: sat.groundMeters,
          viewMeters: groundMeters,
          groundSqMi,
          zoom: sat.zoom,
        };
      });
      setMapBuildings(buildings);
      clearFloorCache();
      setFloorY(null);
      setFloorDebug(null);
      setTileSnapshotLevel(0);
      setContextMode('google');
      setContextNote(null);
      setLot(DEFAULT_LOT);
      setViewLocked(false);
      setLotSelected(false);
      setSelectedVertexIndex(null);
    } catch (err) {
      setSiteError(err?.message || String(err));
    } finally {
      setSiteLoading(false);
    }
  }, [address, groundSqMi]);

  const onLotChange = useCallback((next) => {
    setLot(next);
  }, []);

  const onFloorLock = useCallback((y) => {
    const next = Number(y);
    if (!Number.isFinite(next)) return;
    setFloorY((prev) => {
      if (prev == null || !Number.isFinite(prev)) return next;
      if (next < prev - 0.5) return next;
      return prev;
    });
  }, []);

  // Debug only — do not step floorY down from live samples after lock
  // (that sank the lot through Photorealistic tiles when models/LODs refreshed).
  const onFloorDebug = useCallback((debug) => {
    setFloorDebug(debug);
  }, []);

  const onContextModeChange = useCallback((mode) => {
    setContextMode(mode);
    setContextNote(null);
    if (mode === 'google') {
      clearFloorCache();
      setFloorY(null);
      setTileSnapshotLevel(0);
    } else {
      setFloorY(0);
    }
  }, []);

  /**
   * Photorealistic attempt failed → half / quarter / OSM / default 3D.
   * Docs: docs/PHOTOREALISTIC_MOBILE_FALLBACK.md
   */
  const onTilesAttemptFailed = useCallback(
    (payload) => {
      const step = nextLadderAfterTilesFail(
        tileSnapshotLevel,
        mapBuildings.length,
      );
      console.warn('Photorealistic ladder escalate', payload, step);
      clearFloorCache();
      if (step.kind === 'tiles') {
        setTileSnapshotLevel(step.levelIndex);
        setFloorY(null);
        setContextMode('google');
        setContextNote(
          ladderNoteForStep('tiles', getTileSnapshotBudget(step.levelIndex).id),
        );
        return;
      }
      if (step.kind === 'osm') {
        setContextMode('osm');
        setFloorY(0);
        setContextNote(ladderNoteForStep('osm'));
        return;
      }
      setContextMode('off');
      setFloorY(0);
      setContextNote(ladderNoteForStep('off'));
    },
    [tileSnapshotLevel, mapBuildings.length],
  );

  const resetLotFloor = useCallback(() => {
    clearFloorCache();
    setFloorY(null);
    setFloorDebug(null);
    setLot((prev) => ({ ...prev, y: 0 }));
    setLotSelected(false);
  }, []);

  const applyPreset = useCallback((name) => {
    const next = presetLot(name);
    if (next) {
      setLot(next);
      setSelectedVertexIndex(null);
      setLotSelected(true);
    }
  }, []);

  const siteBooting = Boolean(
    siteLoading ||
      (site &&
        contextMode === 'google' &&
        (floorY == null || !Number.isFinite(floorY))),
  );

  return {
    address,
    setAddress,
    groundSqMi,
    setGroundSqMi,
    siteLoading,
    siteError,
    site,
    lot,
    setLot,
    mapBuildings,
    contextMode,
    setContextMode,
    onContextModeChange,
    tileSnapshotLevel,
    contextNote,
    setContextNote,
    onTilesAttemptFailed,
    viewLocked,
    setViewLocked,
    lotSelected,
    setLotSelected,
    floorY,
    floorDebug,
    setback,
    setSetback,
    northDeg,
    setNorthDeg,
    tool,
    setTool,
    snap,
    setSnap,
    selectedVertexIndex,
    setSelectedVertexIndex,
    loadSite,
    onLotChange,
    onFloorLock,
    onFloorDebug,
    resetLotFloor,
    applyPreset,
    siteBooting,
  };
}
